import { Component } from '@angular/core';
import { environment } from '../../environments/environment';
import { saveAs } from 'file-saver';

import { HttpClient } from '@angular/common/http';
import { ReportData } from '../../interfaces/reporte-response.interface';
import Swal from 'sweetalert2';
import { RegistroService } from '../registro.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerComponent } from './components/date-picker.component';
import { CommonModule } from '@angular/common';
import { months } from '../reporte/modal-ventas/assets/date';

/**
 * Componente encargado de filtrar y consultar reportes históricos por rango de fechas.
 *
 * Permite seleccionar una fecha inicial y final, consultar datos desde el backend
 * y exportar los resultados en un archivo Excel. También incluye soporte para
 * traducciones y un selector de fecha personalizado.
 *
 * @component
 * @selector app-report-no-ventas
 * @standalone
 *
 * @description
 * Este componente muestra un formulario de filtros con rango de fechas y ejecuta
 * solicitudes HTTP para obtener los datos correspondientes. Además, permite exportar
 * la información consultada en formato XLSX y mostrar mensajes de ayuda traducidos.
 */

@Component({
    selector: 'app-report-no-ventas',
    standalone: true,
    imports: [DatePickerComponent, CommonModule, TranslateModule],
    templateUrl: './report-filter-date.component.html',
    styleUrls: ['./report-filter-date.component.css'],
})
export class ReportFilterDateComponent {
    startDate = '';
    endDate = '';
    dataLoaded = false;
    reportData: ReportData[] = [];

    private readonly apiUrl: string = environment.apiSiriUrl;

    constructor(
        private readonly http: HttpClient,
        private readonly registroService: RegistroService,
        private readonly translate: TranslateService
    ) {
        this.translate.setDefaultLang('es');
        this.translate.use('es');
    }

    /**
     * Maneja el evento cuando el usuario selecciona la fecha inicial.
     * También establece la fecha final automáticamente igual a la inicial.
     *
     * @param date Fecha seleccionada en formato YYYY-MM-DD.
     */
    onStartDateSelected(date: string) {
        this.startDate = date;
        this.endDate = this.startDate;
    }

    /**
     * Maneja el evento cuando el usuario selecciona la fecha final.
     *
     * @param date Fecha seleccionada en formato YYYY-MM-DD.
     */
    onEndDateSelected(date: string) {
        this.endDate = date;
    }

    /**
     * Realiza la consulta de los datos históricos según las fechas seleccionadas.
     * Si las fechas no están completas, no se envía la petición.
     */
    toConsult() {
        if (!this.startDate || !this.endDate) return;

        this.http
            .get<
                ReportData[]
            >(`${this.apiUrl}reporte/historico?fechaInicio=${this.startDate}&fechaFin=${this.endDate}&agenteVigiladoID=${this.registroService.empresaSelected}`)
            .subscribe({
                next: (response) => {
                    this.reportData = response.map((reporte) => ({
                        ...reporte,
                        periodoNombre: this.getMonthName(reporte.periodo),
                    }));

                    this.dataLoaded = true;
                },
                error: (error) => {
                    console.error('Error', error);
                },
            });
    }

    /**
     * Exporta los datos del reporte histórico en formato Excel (XLSX).
     * Utiliza la librería FileSaver para descargar el archivo en el navegador.
     */
    exportData() {
        this.http
            .get(
                `${this.apiUrl}reporte/exportar-cargue-historico?fechaInicio=${this.startDate}&fechaFin=${this.endDate}&agenteVigiladoID=${this.registroService.empresaSelected}`,
                { responseType: 'blob' }
            )
            .subscribe({
                next: (response: Blob) => {
                    console.log(response);
                    saveAs(response, 'reporte.xlsx');
                },
                error: (error) => {
                    console.error('Error', error);
                },
            });
    }

    mostrarTexto() {
        this.translate
            .get('reporteHistoricos.alertaAyuda')
            .subscribe((translatedText: string) => {
                Swal.fire('', translatedText, 'info');
            });
    }

    /**
     * Retorna el nombre del mes basado en su número.
     *
     * @param {number} monthNumber - Número del mes (1–12).
     * @returns {string} Nombre del mes en español o una cadena vacía si no existe.
     *
     * @example
     * getMonthName(3); // "Marzo"
     */
    getMonthName(monthNumber: number): string {
        const month = months.find((m) => m.number === Number(monthNumber));
        return month ? month.name : '';
    }
}
